import PropTypes from "prop-types";

/**
 * Indusynq hexagonal logomark.
 * - 6 corners = 6 pillars: Sales, Supply Chain, Manufacturing, Cash, AI Tech, People
 * - Purple → blue gradient with glow
 * - Two variants:
 *    "mark"     → compact, for navbar / favicons (~28-32px)
 *    "labelled" → large, with pillar labels around the hexagon (for hero use)
 */

const PILLARS = [
  { label: "Sales", angle: -90 },        // top
  { label: "AI Tech", angle: -30 },      // top-right
  { label: "Manufacturing", angle: 30 }, // bottom-right
  { label: "Cash", angle: 90 },          // bottom
  { label: "Supply Chain", angle: 150 }, // bottom-left
  { label: "People", angle: 210 },       // top-left
];

// Hexagon vertices at unit radius, "pointy-top" orientation
function vertex(cx, cy, r, angleDeg) {
  const rad = (angleDeg * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

export function LogoMark({ size = 28, glow = true, onDark = false, className = "" }) {
  const cx = 32;
  const cy = 32;
  const rOuter = 26;
  const rInner = 18;

  const outerPts = PILLARS.map((p) => vertex(cx, cy, rOuter, p.angle));
  const innerPts = PILLARS.map((p) => vertex(cx, cy, rInner, p.angle));
  const outerPath = outerPts.map((pt) => pt.join(",")).join(" ");
  const innerPath = innerPts.map((pt) => pt.join(",")).join(" ");

  const gradientId = "indusynq-mark-grad";
  const glowId = "indusynq-mark-glow";
  const strokeGradId = "indusynq-mark-stroke";

  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={className}
      aria-label="Indusynq"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#7C3AED" />
          <stop offset="55%" stopColor="#4F46E5" />
          <stop offset="100%" stopColor="#0EA5E9" />
        </linearGradient>
        <linearGradient id={strokeGradId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#A78BFA" />
          <stop offset="100%" stopColor="#38BDF8" />
        </linearGradient>
        {glow && (
          <filter id={glowId} x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="1.6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        )}
      </defs>

      {/* Outer hexagon — filled gradient with glow */}
      <polygon
        points={outerPath}
        fill={`url(#${gradientId})`}
        filter={glow ? `url(#${glowId})` : undefined}
      />

      {/* Inner hexagon — carved out for lattice look */}
      <polygon
        points={innerPath}
        fill={onDark ? "#0F172A" : "#FFFFFF"}
      />

      {/* Radial spokes hinting at 6 pillars */}
      {outerPts.map(([x, y], i) => (
        <line
          key={i}
          x1={innerPts[i][0]}
          y1={innerPts[i][1]}
          x2={x}
          y2={y}
          stroke={`url(#${strokeGradId})`}
          strokeWidth="1.2"
          strokeLinecap="round"
          opacity="0.85"
        />
      ))}

      {/* Corner accent dots — the six pillars */}
      {outerPts.map(([x, y], i) => (
        <circle key={`dot-${i}`} cx={x} cy={y} r="2.2" fill="#F8FAFC" />
      ))}

      {/* Centre core */}
      <circle cx={cx} cy={cy} r="3.4" fill={`url(#${gradientId})`} />
    </svg>
  );
}

LogoMark.propTypes = {
  size: PropTypes.number,
  glow: PropTypes.bool,
  onDark: PropTypes.bool,
  className: PropTypes.string,
};

export function LogoLabelled({ size = 340, onDark = false, className = "" }) {
  const cx = 320;
  const cy = 220;
  const rOuter = 128;
  const rInner = 90;
  const rLabel = 175;

  const outerPts = PILLARS.map((p) => vertex(cx, cy, rOuter, p.angle));
  const innerPts = PILLARS.map((p) => vertex(cx, cy, rInner, p.angle));
  const outerPath = outerPts.map((pt) => pt.join(",")).join(" ");
  const innerPath = innerPts.map((pt) => pt.join(",")).join(" ");

  const gradientId = "indusynq-lg-grad";
  const strokeGradId = "indusynq-lg-stroke";
  const glowId = "indusynq-lg-glow";

  const labelAnchor = (angle) => {
    if (angle === -90 || angle === 90) return "middle";
    return angle > -90 && angle < 90 ? "start" : "end";
  };

  return (
    <svg
      viewBox="0 0 640 440"
      width={size}
      height={size * (440 / 640)}
      className={className}
      aria-label="Indusynq hexagon"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#7C3AED" />
          <stop offset="55%" stopColor="#4F46E5" />
          <stop offset="100%" stopColor="#0EA5E9" />
        </linearGradient>
        <linearGradient id={strokeGradId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#A78BFA" />
          <stop offset="100%" stopColor="#38BDF8" />
        </linearGradient>
        <filter id={glowId} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="8" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* soft radial halo */}
      <circle cx={cx} cy={cy} r="200" fill={`url(#${gradientId})`} opacity="0.08" />

      {/* Outer hexagon with glow */}
      <polygon
        points={outerPath}
        fill={`url(#${gradientId})`}
        filter={`url(#${glowId})`}
      />
      {/* Inner carve */}
      <polygon points={innerPath} fill={onDark ? "#0F172A" : "#FFFFFF"} />

      {/* Spokes */}
      {outerPts.map(([x, y], i) => (
        <line
          key={i}
          x1={innerPts[i][0]}
          y1={innerPts[i][1]}
          x2={x}
          y2={y}
          stroke={`url(#${strokeGradId})`}
          strokeWidth="2"
          strokeLinecap="round"
          opacity="0.9"
        />
      ))}

      {/* Corner dots + subtle outer ring */}
      {outerPts.map(([x, y], i) => (
        <g key={`n-${i}`}>
          <circle cx={x} cy={y} r="9" fill={onDark ? "#0F172A" : "#FFFFFF"} stroke={`url(#${strokeGradId})`} strokeWidth="2" />
          <circle cx={x} cy={y} r="3.5" fill={`url(#${gradientId})`} />
        </g>
      ))}

      {/* Centre core */}
      <circle cx={cx} cy={cy} r="14" fill={`url(#${gradientId})`} filter={`url(#${glowId})`} />
      <circle cx={cx} cy={cy} r="6" fill={onDark ? "#0F172A" : "#FFFFFF"} />

      {/* Pillar labels */}
      {PILLARS.map((p, i) => {
        const [lx, ly] = vertex(cx, cy, rLabel, p.angle);
        return (
          <text
            key={p.label}
            x={lx}
            y={ly}
            fill={onDark ? "#E2E8F0" : "#0F172A"}
            fontSize="14"
            fontWeight="600"
            letterSpacing="2"
            textAnchor={labelAnchor(p.angle)}
            dominantBaseline="middle"
            style={{ textTransform: "uppercase", fontFamily: "Inter, sans-serif" }}
          >
            {p.label}
          </text>
        );
      })}
    </svg>
  );
}

LogoLabelled.propTypes = {
  size: PropTypes.number,
  onDark: PropTypes.bool,
  className: PropTypes.string,
};

export default LogoMark;
